import { Box } from '@apia/theme';
import dayjs from 'dayjs';
import React from 'react';
import { shallowEqual } from 'react-redux';
import { FaTrashAlt } from '@meronex/icons/fa';
import { getDateFormat } from '../../../../utils/date';
import { TDateFormat } from '../../../../types';
import DateInput from '../../../common/fields/DateInput';
import { SimpleButton } from '../../../common';
import store from '../../../../store';
import { useAppSelector } from '../../../../hooks/hooks';
import ApiaApi, { makeApiaUrl } from '../../../../api/apiaApi/apiaApi';
import { TScheduleResult } from './types';
import { noNaN } from '../../../../utils/util';
import { schedulerActions } from '../../../../store/schedulerSlice';
import makeDaysStage from './mini/utils';

const dateFormat: TDateFormat = getDateFormat();
interface ISchedulerNavigation {
  // currentDate: string;
  onLoadWeekDay: (weekDay: string) => unknown;
  mondayWeek: string;
}

export function getCurrentDate() {
  return dayjs().format(dateFormat);
}

const SchedulerNavigation: React.FC<ISchedulerNavigation> = ({
  mondayWeek,
  onLoadWeekDay,
}) => {
  const onLastHandler = () => {
    store.dispatch(schedulerActions.setState({ isLoading: 'LastWeek' }));
    onLoadWeekDay(
      dayjs(mondayWeek, dateFormat).subtract(7, 'day').format(dateFormat),
    );
  };
  const onTodayHandler = () => {
    store.dispatch(schedulerActions.setState({ isLoading: 'Today' }));
    const { data } = store.getState().schedulerSlice;
    makeDaysStage(data);
    onLoadWeekDay(dayjs().subtract(1, 'day').format(dateFormat));
  };
  const onNextHandler = () => {
    store.dispatch(schedulerActions.setState({ isLoading: 'NextWeek' }));

    onLoadWeekDay(
      dayjs(mondayWeek, dateFormat).add(7, 'day').format(dateFormat),
    );
  };
  const onDeleteHandler = () => {
    store.dispatch(schedulerActions.setState({ isLoading: 'DeleteWeek' }));
    ApiaApi.get<TScheduleResult>(
      makeApiaUrl({
        ajaxUrl: '/apia.design.TaskSchedulerAction.run',
        action: 'clearExecutionSchedulerSelectedDay',
      }),
    )
      .then((response) => {
        if (response?.data?.success) {
          store.dispatch(
            schedulerActions.setState({
              selectedDay: {},
              selectedCell: null,
              labelSelectedDay: { hour: '', day: '' },
              isLoading: 'NotLoading',
            }),
          );
          const { data } = store.getState().schedulerSlice;
          makeDaysStage(data);
        }
      })
      .catch(() => {});
  };

  const {
    isDisabled,
    isTodayLoading,
    isLastWeekLoading,
    isDateInputLoading,
    isNextWeekLoading,
    isDeleteLoading,
  } = useAppSelector((global) => {
    const state = global.schedulerSlice;
    return {
      isDisabled: state?.isLoading !== 'NotLoading',
      isTodayLoading: state?.isLoading === 'Today',
      isLastWeekLoading: state?.isLoading === 'LastWeek',
      isDateInputLoading: state?.isLoading === 'DateInput',
      isNextWeekLoading: state?.isLoading === 'NextWeek',
      isDeleteLoading: state?.isLoading === 'DeleteWeek',
    };
  }, shallowEqual);

  const parsedDate = React.useCallback(() => {
    if (mondayWeek === '') return '';

    const parsedMondayWeek = dayjs(mondayWeek, dateFormat).get('date');
    const parsedSundayWeek = dayjs(mondayWeek, dateFormat)
      .add(6, 'day')
      .get('date');
    const mondayWeekMonth = noNaN(dayjs(mondayWeek, dateFormat).get('month'));
    const sundayWeekMonth = noNaN(
      dayjs(mondayWeek, dateFormat).add(6, 'day').get('month'),
    );

    let parsedDay;
    if (
      parsedMondayWeek > parsedSundayWeek &&
      mondayWeekMonth < sundayWeekMonth
    ) {
      parsedDay = `${String(noNaN(parsedMondayWeek))} de ${
        window.LBL_MONTHS[mondayWeekMonth]
      } - ${String(noNaN(parsedSundayWeek))} de ${
        window.LBL_MONTHS[sundayWeekMonth]
      }, ${noNaN(dayjs(mondayWeek, dateFormat).get('year'))} `;
    } else if (mondayWeekMonth > sundayWeekMonth) {
      parsedDay = `${String(noNaN(parsedMondayWeek))} de ${
        window.LBL_MONTHS[mondayWeekMonth]
      }, ${noNaN(dayjs(mondayWeek, dateFormat).get('year'))} - ${String(
        noNaN(parsedSundayWeek),
      )} de ${window.LBL_MONTHS[sundayWeekMonth]}, ${noNaN(
        dayjs(mondayWeek, dateFormat).add(1, 'year').get('year'),
      )} `;
    } else {
      parsedDay = `${String(noNaN(parsedMondayWeek))} - ${String(
        noNaN(parsedSundayWeek),
      )} de ${
        window.LBL_MONTHS[noNaN(dayjs(mondayWeek, dateFormat).get('month'))]
      }, ${noNaN(dayjs(mondayWeek, dateFormat).get('year'))}`;
    }

    return parsedDay;
  }, [mondayWeek]);

  return (
    <>
      <Box className="scheduler__navigator">
        <SimpleButton
          {...window.getVariant('outline')}
          className="scheduler__navigator_todayBtn"
          disabled={isDisabled}
          aria-label={window.LBL_TODAY}
          isLoading={isTodayLoading}
          onClick={React.useCallback(onTodayHandler, [onLoadWeekDay])}
          type="button"
        >
          {window.LBL_TODAY}
        </SimpleButton>
        <SimpleButton
          {...window.getVariant('outline')}
          className="scheduler__navigator_lastBtn"
          disabled={isDisabled}
          title={window.LBL_SCHED_LAST_WEEK_TOOLTIP}
          aria-label={window.LBL_SCHED_LAST_WEEK_TEXT}
          isLoading={isLastWeekLoading}
          onClick={React.useCallback(onLastHandler, [
            mondayWeek,
            onLoadWeekDay,
          ])}
          type="button"
        >
          &lt;
        </SimpleButton>
        <DateInput
          className="scheduler__navigator_dateInput"
          disabled={isDisabled}
          isLoading={isDateInputLoading}
          getCustomMask={React.useCallback(() => {
            return '';
          }, [])}
          renderLabel={parsedDate}
          onChange={(newDate) => {
            if (newDate) {
              store.dispatch(
                schedulerActions.setState({ isLoading: 'DateInput' }),
              );
              onLoadWeekDay(dayjs(newDate, dateFormat).format(dateFormat));
            }
          }}
          value={mondayWeek}
        />
        <SimpleButton
          {...window.getVariant('outline')}
          className="scheduler__navigator_nextBtn"
          disabled={isDisabled}
          title={window.LBL_SCHED_NEXT_WEEK_TOOLTIP}
          aria-label={window.LBL_SCHED_NEXT_WEEK_TEXT}
          isLoading={isNextWeekLoading}
          onClick={React.useCallback(onNextHandler, [
            mondayWeek,
            onLoadWeekDay,
          ])}
          type="button"
        >
          &gt;
        </SimpleButton>
        <SimpleButton
          {...window.getVariant('outline')}
          title={window.LBL_SCHED_RESET_TOOLTIP}
          aria-label={window.LBL_SCHED_RESET_TEXT}
          className="scheduler__navigator_DeleteBtn"
          disabled={isDisabled}
          isLoading={isDeleteLoading}
          onClick={React.useCallback(onDeleteHandler, [])}
          type="button"
        >
          <FaTrashAlt />
        </SimpleButton>
      </Box>
    </>
  );
};

export default SchedulerNavigation;
